import { ReferenceLine } from "recharts"
import { shortDate } from "@/lib/format"
import { type ActionMark, badgeText } from "../lib/actions"

// The Push review actions on the graphs: a dashed line with a numbered circle
// on each point an action falls on, and a key under the graph saying what
// each number is, so long action names never collide.

// Room above the plot for the circles.
export const MARKED_TOP = 24

function ActionBadge({ text }: { text: string }) {
  return (
    <span className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-foreground px-1 text-[11px] font-semibold text-background tabular-nums">
      {text}
    </span>
  )
}

function BadgeLabel({ viewBox, text }: { viewBox?: { x: number; y: number }; text: string }) {
  if (!viewBox) return null
  const width = Math.max(18, text.length * 6.5 + 8)
  return (
    <g transform={`translate(${viewBox.x}, ${viewBox.y - 11})`}>
      <rect x={-width / 2} y={-9} width={width} height={18} rx={9} fill="var(--foreground)" />
      <text textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600} fill="var(--background)">{text}</text>
    </g>
  )
}

// Called as a function, not a component: recharts only reads its direct
// children.
export function actionLines(marks: ActionMark[]) {
  return marks.map((mark) => (
    <ReferenceLine
      key={`action-${mark.x}`}
      x={mark.x}
      stroke="var(--foreground)"
      strokeDasharray="4 3"
      label={(props: { viewBox?: { x: number; y: number } }) => <BadgeLabel viewBox={props.viewBox} text={badgeText(mark.actions)} />}
    />
  ))
}

export function ActionKey({ marks, today }: { marks: ActionMark[]; today: string }) {
  const actions = marks.flatMap((mark) => mark.actions)
  if (actions.length === 0) return null
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm text-foreground">
      {actions.map((a) => (
        <span key={a.n} className="flex items-center gap-1.5">
          <ActionBadge text={String(a.n)} />
          <span className="font-medium">{a.type}</span>
          <span>· {shortDate(a.date, today)}</span>
        </span>
      ))}
    </div>
  )
}
